/**
 * Survey Intelligence — Frontend types
 *
 * @remarks
 * Mirrors the backend entities for sessions, files and processing status.
 *
 * @module survey/types
 */

export type SurveySessionStatus =
  | 'draft'
  | 'uploading'
  | 'processing'
  | 'processed'
  | 'failed'
  | 'archived';

export type SurveyCaptureMode = 'vehicle' | 'bicycle' | 'pedestrian' | 'drone' | 'static';

export type SurveyFileType = 'video' | 'photo_sequence' | 'gpx';

// ─── Session ─────────────────────────────────────────────────────────────────

export interface SurveySession {
  id: string;
  projectId: string;
  name: string;
  status: SurveySessionStatus;
  captureDate: string | null;
  createdById: string | null;
  createdAt: string;
  updatedAt: string;
}

// ─── Source file ──────────────────────────────────────────────────────────────

export interface SurveySourceFile {
  id: string;
  sessionId: string;
  fileType: SurveyFileType;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  status: 'pending' | 'stored' | 'failed';
  storageKey: string;
  createdAt: string;
}

// ─── Capture ──────────────────────────────────────────────────────────────────

export interface SurveyCapture {
  id: string;
  sequenceIndex: number;
  distanceFromStartM: number;
  cameraLat: number;
  cameraLon: number;
  cameraHeading: number | null;
  captureTimestamp: string | null;
  storageKey: string;
  gpsInterpolated: boolean;
}

export interface SurveyCapturesPage {
  captures: SurveyCapture[];
  total: number;
  page: number;
  limit: number;
}

// ─── Observation ──────────────────────────────────────────────────────────────

export type ObservationStatus = 'draft' | 'confirmed' | 'exported';

export type ObservationObjectType =
  | 'traffic_light'
  | 'tree'
  | 'waste_container'
  | 'streetlight'
  | 'traffic_sign'
  | 'urban_furniture'
  | 'sidewalk_issue'
  | 'other';

export const OBSERVATION_TYPE_LABELS: Record<ObservationObjectType, string> = {
  traffic_light: 'Semáforo',
  tree: 'Árbol',
  waste_container: 'Contenedor',
  streetlight: 'Luminaria',
  traffic_sign: 'Señal vial',
  urban_furniture: 'Mobiliario urbano',
  sidewalk_issue: 'Problema en vereda',
  other: 'Otro',
};

export type PlacementMethod = 'camera_fallback' | 'manual_click' | 'manual_drag';

export interface SurveyObservation {
  id: string;
  sessionId: string;
  captureId: string | null;
  description: string;
  objectType: ObservationObjectType | null;
  status: ObservationStatus;
  obsLat: number;
  obsLon: number;
  observedBy: string | null;
  exportedEntityId: string | null;
  // Fase 5.0 — Spatial placement
  imageX: number | null;
  imageY: number | null;
  placementMethod: PlacementMethod | null;
  estimatedLat: number | null;
  estimatedLon: number | null;
  confidence: number | null;
  manuallyAdjusted: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateObservationPayload {
  sessionId: string;
  captureId?: string;
  description: string;
  objectType?: ObservationObjectType;
  obsLat: number;
  obsLon: number;
  // Fase 5.0 — optional spatial placement fields
  imageX?: number;
  imageY?: number;
  placementMethod?: PlacementMethod;
  estimatedLat?: number;
  estimatedLon?: number;
  confidence?: number;
  manuallyAdjusted?: boolean;
}

export interface UpdateObservationPayload {
  description?: string;
  objectType?: ObservationObjectType;
  status?: ObservationStatus;
  obsLat?: number;
  obsLon?: number;
  // Fase 5.0 — Spatial placement
  imageX?: number;
  imageY?: number;
  placementMethod?: PlacementMethod;
  estimatedLat?: number;
  estimatedLon?: number;
  confidence?: number;
  manuallyAdjusted?: boolean;
}

// ─── Payloads ─────────────────────────────────────────────────────────────────

export interface CreateSessionPayload {
  projectId: string;
  name: string;
  captureMode: SurveyCaptureMode;
  captureDate?: string;
  cameraHeightM?: number;
  captureDevice?: string;
}
